import { useEffect, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { ArrowRight, Sparkles } from 'lucide-react';
import { cn } from '@jeraisonne/ui';
import { activePromo, promoEndLabel, promoHeadline, promoTimeLeft } from '@jeraisonne/db/offers';
import { useOfferSettings } from '@/lib/offers/offer-settings';

/** Rafraîchit l'heure chaque seconde (compte à rebours de la promo). */
function useNow(): Date {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), 1_000);
    return () => window.clearInterval(id);
  }, []);
  return now;
}

export interface CountdownPart {
  value: string;
  unit: string;
}

/** Cases du compte à rebours : jours (s'il en reste), heures, minutes, secondes. PUR. */
export function countdownParts(endsAt: string, now: Date): CountdownPart[] | null {
  const ms = Date.parse(endsAt) - now.getTime();
  if (!Number.isFinite(ms) || ms <= 0) return null;
  const sec = Math.floor(ms / 1000);
  const days = Math.floor(sec / 86_400);
  const pad = (n: number) => String(n).padStart(2, '0');
  const parts: CountdownPart[] = [
    { value: pad(Math.floor((sec % 86_400) / 3600)), unit: 'h' },
    { value: pad(Math.floor((sec % 3600) / 60)), unit: 'min' },
    { value: pad(sec % 60), unit: 's' },
  ];
  return days > 0 ? [{ value: String(days), unit: days > 1 ? 'jours' : 'jour' }, ...parts] : parts;
}

/**
 * Promo SKILLCARDS en cours (admin « Tarifs », migration 0717) : bandeau doré
 * avec le pourcentage, la date de fin et le temps restant. Rien sans promo.
 * Les prix des offres sont déjà remisés par le serveur (`skillcard_quote`).
 */
export function SkillcardPromoBanner({
  to,
  ctaLabel = 'Voir les SKILLCARDS',
  className,
}: {
  /** Lien vers les offres (tableaux de bord) ; absent sur les pages d'achat. */
  to?: string;
  ctaLabel?: string;
  className?: string;
}) {
  const settings = useOfferSettings();
  const now = useNow();
  const promo = activePromo(settings, now);
  if (!promo) return null;
  const left = promoTimeLeft(promo.ends_at, now);
  const parts = countdownParts(promo.ends_at, now);
  return (
    <section
      aria-label="Promotion SKILLCARDS"
      className={cn(
        'rounded-2xl bg-[linear-gradient(135deg,#f6c445_0%,#f0a92b_100%)] p-4 text-[#061433] shadow-md',
        className,
      )}
    >
      <div className="flex items-start gap-3">
        <span
          aria-hidden
          className="font-display flex h-12 min-w-12 shrink-0 items-center justify-center rounded-xl bg-[#061433] px-2 text-lg font-extrabold text-[#f6c445]"
        >
          −{promo.percent}%
        </span>
        <div className="min-w-0 flex-1">
          <p className="font-display flex items-center gap-1.5 text-base font-extrabold leading-tight">
            <Sparkles className="h-4 w-4 shrink-0" aria-hidden />
            {promoHeadline(promo)}
          </p>
          <p className="mt-1 text-sm font-medium">
            Cartes, packs et programmes : le prix baisse tout seul {promoEndLabel(promo.ends_at)}.
          </p>
        </div>
      </div>
      {parts && (
        <div className="mt-3">
          <p className="text-xs font-bold uppercase tracking-wider">Fin de l’offre dans</p>
          <div role="timer" className="mt-1.5 flex gap-1.5 sm:gap-2">
            <span className="sr-only">plus que {left}</span>
            {parts.map((p) => (
              <span
                key={p.unit}
                aria-hidden
                className="flex min-w-0 flex-1 flex-col items-center rounded-xl bg-[#061433] px-1 py-2 text-[#f6c445] sm:flex-none sm:px-4"
              >
                <span className="font-display text-2xl font-extrabold tabular-nums leading-none sm:text-3xl">
                  {p.value}
                </span>
                <span className="mt-1 text-xs font-bold text-white/80">{p.unit}</span>
              </span>
            ))}
          </div>
        </div>
      )}
      {to && (
        <Link
          to={to}
          className="font-display mt-3 inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-xl bg-[#061433] px-4 text-sm font-bold text-white active:scale-[0.98] sm:w-auto"
        >
          {ctaLabel}
          <ArrowRight className="h-4 w-4" aria-hidden />
        </Link>
      )}
    </section>
  );
}
